import React, { useMemo, useEffect, useState } from 'react'
import { StorefrontCarouselSlide } from '../../../models/homepage-carousel-slide'
import HomepageCarouselCard from './HomepageCarouselCard'
import * as S from './styles'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'

// Number of cloned slides prepended + appended for seamless infinite loop.
const CLONE_COUNT = 4

interface HomepageCarouselProps {
    slides: StorefrontCarouselSlide[]
}

const HomepageCarousel: React.FC<HomepageCarouselProps> = ({ slides }) => {
    const [rawIndex, setRawIndex] = useState(CLONE_COUNT)
    const [animated, setAnimated] = useState(true)

    // Reset to first slide when slides array changes (channel switch)
    useEffect(() => {
        setRawIndex(CLONE_COUNT)
    }, [slides])

    // Extended track: [last-4 clones] + [real slides] + [first-4 clones]
    const extended = useMemo(() => {
        if (!slides.length) return []
        return [
            ...slides.slice(-CLONE_COUNT),
            ...slides,
            ...slides.slice(0, CLONE_COUNT),
        ]
    }, [slides])

    // After a silent teleport (animated=false), re-enable transition on next frame.
    useEffect(() => {
        if (!animated) {
            const id = requestAnimationFrame(() => setAnimated(true))
            return () => cancelAnimationFrame(id)
        }
    }, [animated])

    if (!slides.length) return null

    const total = slides.length
    const totalExtended = extended.length

    const handleNext = () => setRawIndex((i) => i + 1)
    const handlePrev = () => setRawIndex((i) => i - 1)

    const handleTransitionEnd = () => {
        if (rawIndex >= CLONE_COUNT + total) {
            setAnimated(false)
            setRawIndex(rawIndex - total)
        } else if (rawIndex < CLONE_COUNT) {
            setAnimated(false)
            setRawIndex(rawIndex + total)
        }
    }

    return (
        <S.Section>
            <S.Heading>Explore What&apos;s Featured &ndash; Shape It as You Go</S.Heading>

            <S.TrackViewport>
                <S.Track
                    style={{
                        '--n': totalExtended,
                        transform: `translateX(calc(-${rawIndex} * 100% / var(--n)))`,
                        transition: animated ? `transform 0.38s ease` : 'none',
                    } as React.CSSProperties}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {extended.map((slide, i) => (
                        <S.SlideItem key={i}>
                            <HomepageCarouselCard slide={slide} />
                        </S.SlideItem>
                    ))}
                </S.Track>
            </S.TrackViewport>

            <S.NavRow>
                <S.NavButton aria-label="Previous slide" onClick={handlePrev}>
                    <ChevronLeft />
                </S.NavButton>
                <S.NavButton aria-label="Next slide" onClick={handleNext}>
                    <ChevronRight />
                </S.NavButton>
            </S.NavRow>
        </S.Section>
    )
}

export default HomepageCarousel
